import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { CircularProgress } from 'src/components/CircularProgress';
import { Container } from 'src/components/Container';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Meta } from 'src/components/Meta';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import {
  fetchLeaderboard,
  fetchLeaderboardDivisionResults,
  fetchLeaderboardSimilarResults,
} from 'src/models/leaderboard/leaderboard';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';

import { LeaderboardFilterButton } from './components/LeaderboardFilterButton';
import { LeaderboardMe } from './components/LeaderboardMe';
import { LeaderboardResultsList } from './components/LeaderboardResultsList';
import { computeMeResult } from './components/leaderboardUtils';
import { useLeaderboardFilters } from './components/useLeaderboardFilters';

const PER_PAGE = 10;

export const LeaderboardDivisionPage = () => {
  const { id, type, division } = useParams<{
    id: string;
    type: string;
    division: string;
  }>();
  const { user } = useSession();
  const [searchParams] = useSearchParams();
  const tvMode = searchParams.get('tvMode') === 'true';
  const { t } = useTranslation('translation', {
    keyPrefix: 'leaderboard.public',
  });

  const [page, setPage] = useState(1);
  const { filters } = useLeaderboardFilters();

  const { data: leaderboard } = useQuery({
    queryKey: ['leaderboard', id],
    queryFn: () => fetchLeaderboard(id!),
    enabled: !!id,
  });

  const divisionObj = leaderboard?.divisions.find(
    (d) => d.key === division && d.type === type
  );

  const { data: resultsData, isLoading } = useQuery({
    queryKey: ['leaderboardDivisionResults', id, type, division, page, filters],
    queryFn: () =>
      fetchLeaderboardDivisionResults(id!, {
        type: type === 'all' ? 'null' : type,
        key: division === 'all' ? 'null' : division,
        page,
        per: PER_PAGE,
        ...filters,
      }),
    enabled: !!id,
  });

  const { data: similarResults } = useQuery({
    queryKey: ['leaderboardSimilarResults', id],
    queryFn: () => fetchLeaderboardSimilarResults(id!),
    enabled: !!id,
  });

  const results = (resultsData?.results || []).map((r) => ({
    ...r,
    date: typeof r.date === 'number' ? r.date : r.date,
  }));
  const total = resultsData?.meta?.total || 0;
  const totalPages = Math.ceil(total / PER_PAGE);

  const me = computeMeResult(
    user?.id,
    similarResults || [],
    total,
    leaderboard?.sortAsc || false
  );

  const hasNoFilters =
    !filters.ageMin &&
    !filters.ageMax &&
    !filters.gender &&
    !filters.locationId &&
    !filters.locationIds?.length &&
    !filters.locationTags?.length;

  const showMe = !!me && hasNoFilters;

  const title = divisionObj?.label || leaderboard?.name || '';
  const description =
    divisionObj?.description || leaderboard?.description || '';
  const backLabel = divisionObj ? leaderboard?.name : t('title');

  return (
    <>
      <Meta title={title} />
      <AppFrameConfig
        id="leaderboard-division"
        containerMaxWidth="lg"
        title={title}
        backButtonLinkTo={
          divisionObj ? `/leaderboards/public/${id}` : '/leaderboards/public'
        }
        backButtonTitle={backLabel || ''}
      />
      <Container>
        {leaderboard?.imageUrl && (
          <Box
            component="img"
            src={leaderboard.imageUrl}
            sx={{ width: '100%', borderRadius: 0 }}
          />
        )}

        <Box sx={{ mx: 2, my: 3 }}>
          <Typography variant="h6">{title}</Typography>
          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>

        {showMe && <LeaderboardMe me={me} />}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <LeaderboardResultsList
              results={results}
              tvMode={tvMode}
              label={divisionObj?.label}
              leaderboard={leaderboard}
            />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                mt: 2,
              }}
            >
              <Typography variant="body2" sx={{ mr: 1 }}>
                {t('pagination.page', { page })}
              </Typography>
              <IconButton
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <Icon icon={faChevronLeft} />
              </IconButton>
              <IconButton
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <Icon icon={faChevronRight} />
              </IconButton>
            </Box>
          </>
        )}

        {leaderboard && (
          <LeaderboardFilterButton
            availableFilters={leaderboard.filters}
            ageFilters={leaderboard.ageFilters}
          />
        )}
      </Container>
    </>
  );
};
